import { useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import Tag from "@/components/base/Tag";
import Reveal from "@/components/base/Reveal";
import { threads } from "@/mocks/homeData";

const tagVariantMap: Record<string, "primary" | "accent" | "secondary"> = {
  直播事故: "primary",
  数据注水: "accent",
  粉丝互撕: "primary",
  剪辑造假: "primary",
  深夜致歉: "secondary",
  账号封禁: "secondary",
};

export default function CommunityThreads() {
  const [liked, setLiked] = useState<number[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [notice, setNotice] = useState("");

  const toggleLike = (id: number) => {
    setLiked((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleCompose = () => {
    setNotice("已记录这条讨论草稿（演示环境，不会真正发布）。");
    setDraft("");
    setComposeOpen(false);
  };

  return (
    <section id="community" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <SectionHeading
          eyebrow="社区讨论"
          eyebrowIcon="ri-chat-3-line"
          title="看完了想说话，这里可以接着聊"
          desc="有人整理时间线，有人补充细节，也有人单纯来看热闹。发言前先翻一下前几楼，别重复。"
          action={
            <button
              type="button"
              onClick={() => setComposeOpen(true)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-edit-2-line"></i>
              </span>
              发起讨论
            </button>
          }
        />

        {notice ? (
          <div className="mb-5 flex items-center gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-2.5 text-xs text-accent-800">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-checkbox-circle-line"></i>
            </span>
            {notice}
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {threads.map((thread, index) => {
            const isLiked = liked.includes(thread.id);
            return (
              <Reveal key={thread.id} delay={index * 50} className="h-full">
                <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-primary-300">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-200 text-secondary-700">
                      <i className={thread.avatarIcon}></i>
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-950">{thread.author}</p>
                      <p className="text-[11px] text-secondary-600">{thread.time}</p>
                    </div>
                    <Tag variant={tagVariantMap[thread.tag] ?? "secondary"}>{thread.tag}</Tag>
                  </div>

                  <div className="mt-4 flex gap-3">
                    <div className="min-w-0 flex-1">
                      <a
                        href="#community"
                        className="block text-sm font-medium leading-6 text-foreground-950 transition-colors hover:text-primary-600"
                      >
                        {thread.title}
                      </a>
                    </div>
                    {thread.thumb ? (
                      <span className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={thread.thumb}
                          alt={`${thread.tag} · 讨论配图`}
                          title="黑料国产 社区讨论配图"
                          className="h-full w-full object-cover object-top"
                        />
                      </span>
                    ) : null}
                  </div>

                  <p className="mt-2.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">{thread.excerpt}</p>

                  <div className="mt-4 flex items-center gap-5 border-t border-background-200 pt-4 text-xs text-secondary-600">
                    <span className="flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-chat-3-line"></i>
                      </span>
                      {thread.replies}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleLike(thread.id)}
                      className={`flex cursor-pointer items-center gap-1 transition-colors ${
                        isLiked ? "text-primary-600" : "hover:text-primary-600"
                      }`}
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={isLiked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                      </span>
                      {thread.likes + (isLiked ? 1 : 0)}
                    </button>
                    <span className="ml-auto flex items-center gap-1">
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-share-forward-line"></i>
                      </span>
                      转发
                    </span>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>

      {composeOpen ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
          <div className="w-full max-w-lg rounded-t-lg border border-background-200 bg-background-50 p-5 sm:rounded-lg md:p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">发起讨论</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setComposeOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-600 transition-colors hover:bg-background-100"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              带上时间点或截图来源，线索更容易被采纳。演示环境不会真正提交。
            </p>
            <textarea
              value={draft}
              maxLength={500}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="说说你看到的现场细节……"
              rows={5}
              className="mt-4 w-full resize-none rounded-md border border-background-300 bg-background-100 p-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
            <div className="mt-1 text-right text-[11px] text-secondary-600">{draft.length} / 500</div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setComposeOpen(false)}
                className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleCompose}
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                发布讨论
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}